// Logik der Passwortverwaltung ohne Oberfläche: Verschlüsselung, Excel-Import, Auswertungen.
//
// Sicherheitskonzept: Alle Einträge werden im Browser mit AES-256-GCM verschlüsselt. Der
// Schlüssel wird per PBKDF2 (SHA-256, 600.000 Runden) aus dem Master-Passwort abgeleitet und
// existiert nur im Arbeitsspeicher. In Firestore liegen ausschließlich Salt, IV und Chiffrat –
// ohne Master-Passwort sind die Daten nicht lesbar (auch nicht für Admins der Datenbank).

const enc = new TextEncoder();
const dec = new TextDecoder();
export const ITERATIONEN = 600000;
const PRUEFTEXT = "schreibtisch-tresor-v1";

function b64(bytes) {
  let s = "";
  new Uint8Array(bytes).forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s);
}
function vonB64(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

export function neuesSalt() {
  return b64(crypto.getRandomValues(new Uint8Array(16)));
}

export async function schluesselAbleiten(masterPasswort, saltB64, iterationen = ITERATIONEN) {
  const basis = await crypto.subtle.importKey("raw", enc.encode(masterPasswort), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: vonB64(saltB64), iterations: iterationen, hash: "SHA-256" },
    basis, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]
  );
}

export async function verschluesseln(schluessel, daten) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, schluessel, enc.encode(JSON.stringify(daten)));
  return { iv: b64(iv), ct: b64(ct) };
}

export async function entschluesseln(schluessel, { iv, ct }) {
  const klar = await crypto.subtle.decrypt({ name: "AES-GCM", iv: vonB64(iv) }, schluessel, vonB64(ct));
  return JSON.parse(dec.decode(klar));
}

// Prüfwert, mit dem beim Entsperren festgestellt wird, ob das Master-Passwort stimmt
export const pruefwertErzeugen = (schluessel) => verschluesseln(schluessel, PRUEFTEXT);
export async function pruefwertOk(schluessel, pruefwert) {
  try { return (await entschluesseln(schluessel, pruefwert)) === PRUEFTEXT; } catch { return false; }
}

// --- Passwortgenerator und -bewertung --------------------------------------------------------
export function passwortErzeugen(laenge = 20, { sonderzeichen = true } = {}) {
  const zeichen = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789" + (sonderzeichen ? "!#$%&*+-=?@_" : "");
  const zufall = crypto.getRandomValues(new Uint32Array(laenge));
  return Array.from(zufall, (z) => zeichen[z % zeichen.length]).join("");
}

// 0 = leer, 1 = schwach, 2 = mittel, 3 = stark
export function passwortStaerke(pw) {
  if (!pw) return 0;
  let klassen = 0;
  if (/[a-z]/.test(pw)) klassen++;
  if (/[A-Z]/.test(pw)) klassen++;
  if (/\d/.test(pw)) klassen++;
  if (/[^A-Za-z0-9]/.test(pw)) klassen++;
  if (pw.length >= 14 && klassen >= 3) return 3;
  if (pw.length >= 10 && klassen >= 2) return 2;
  return 1;
}

// --- Kategorien -----------------------------------------------------------------------------
export const KATEGORIEN = [
  { key: "zugang", label: "Zugänge", icon: "🔑" },
  { key: "hosting", label: "Hosting & Homepages", icon: "🌐" },
  { key: "bank", label: "Bank & Zahlung", icon: "🏦" },
  { key: "pin", label: "PIN & Karten", icon: "🔢" },
  { key: "software", label: "Software & Lizenzen", icon: "💿" },
  { key: "mail", label: "E-Mail", icon: "📧" },
  { key: "geraete", label: "Geräte, WLAN & Telefon", icon: "📶" },
];

export function kategorieAusArt(art) {
  const a = String(art || "").toLowerCase();
  if (/bank|kredit|zahlung|trading|konto/.test(a)) return "bank";
  if (/\bpin\b|pin /.test(a)) return "pin";
  if (/kas|ionos|ftp|homepage|\bwp\b|members|datensic|alte seite|hosting|domain/.test(a)) return "hosting";
  if (/software|plugin|theme|lizenz|divi|\bapp\b/.test(a)) return "software";
  if (/mail/.test(a)) return "mail";
  if (/wlan|kamera|telefon|handy|^[\d\s/+]{6,}$/.test(a)) return "geraete";
  return "zugang";
}

// --- Excel-Import ---------------------------------------------------------------------------
const istUrl = (s) => {
  const t = String(s || "").trim();
  return !!t && !/\s/.test(t) && !t.includes("@") && (/^https?:\/\//i.test(t) || /^www\./i.test(t) || /^[\w-]+(\.[\w-]+)*\.[a-z]{2,6}(\/\S*)?$/i.test(t));
};
const sauber = (v) => (v == null ? "" : String(v).replace(/ /g, " ").trim());
const zeilen = (...teile) => teile.filter(Boolean).join("\n");

function leererEintrag() {
  return { titel: "", kategorie: "zugang", typ: "", organisation: "", benutzer: "", passwort: "", url: "", notizen: "", favorit: false, quelle: "" };
}

// Blatt 1 (Listen-Export): ID, Titel, Bezeichnung, Art, Kunde, USER, Benutzername, Passwort,
// Bemerkung, URL, Info … Die Benutzerspalten enthalten teils URLs – diese wandern ins URL-Feld.
export function listeImportieren(zeilenObjekte) {
  return zeilenObjekte.map((r) => {
    const e = leererEintrag();
    const g = (k) => sauber(r[k]);
    const notizen = [];
    const art = g("Art");
    e.quelle = "Passwortliste";
    e.typ = art.replace(/^zu+a?gn?g/i, "Zugang").replace(/^zugang\b/i, "Zugang");
    e.kategorie = kategorieAusArt(art);
    e.organisation = g("Kunde");
    e.passwort = g("Passwort");

    const urls = [g("URL"), g("USER"), g("Benutzername"), g("Bezeichnung")].filter(istUrl);
    const benutzer = [g("Benutzername"), g("USER")].filter((v) => v && !istUrl(v));
    e.url = urls[0] || "";
    urls.slice(1).filter((u, i, a) => u !== e.url && a.indexOf(u) === i).forEach((u) => notizen.push(`Weitere URL: ${u}`));
    // Spalte "URL" enthält teils Text/Mail statt einer Adresse – nicht verwerfen
    if (g("URL") && !istUrl(g("URL"))) notizen.push(`URL-Feld: ${g("URL")}`);
    e.benutzer = benutzer[0] || "";
    if (benutzer[1] && benutzer[1] !== benutzer[0]) notizen.push(`Weiterer Benutzer: ${benutzer[1]}`);

    e.titel = (!istUrl(g("Bezeichnung")) && g("Bezeichnung")) || g("Titel") || e.organisation || e.url.replace(/^https?:\/\/(www\.)?/i, "").split("/")[0] || "Ohne Titel";
    if (g("Titel") && g("Titel") !== e.titel) notizen.push(`Titel: ${g("Titel")}`);
    if (g("Complianceobjekt-ID")) notizen.push(`Complianceobjekt-ID: ${g("Complianceobjekt-ID")}`);
    e.notizen = zeilen(g("Bemerkung"), g("Info"), ...notizen);
    return e;
  });
}

// Blatt 2 (Homepages/Hosting): frei gestaltet. Eine Zeile mit Eintrag in Spalte A beginnt einen
// neuen Block; Folgezeilen ohne A gehören dazu. Erste Zeile: B = Provider, C = Passwort,
// D = Nutzer. Alles andere (E–I und Folgezeilen) wird beschriftet in die Notizen übernommen.
export function homepageImportieren(rohzeilen) {
  const kopf = (rohzeilen[0] || []).map(sauber);
  const spaltenName = (i) => kopf[i] || `Spalte ${String.fromCharCode(65 + i)}`;
  const bloecke = [];
  let aktuell = null;
  rohzeilen.slice(1).forEach((r) => {
    const zellen = (r || []).map(sauber);
    if (!zellen.some(Boolean)) return;
    if (zellen[0]) { aktuell = { zeilen: [zellen] }; bloecke.push(aktuell); }
    else if (aktuell) aktuell.zeilen.push(zellen);
    else { aktuell = { zeilen: [zellen] }; bloecke.push(aktuell); }
  });

  return bloecke.map(({ zeilen: zs }) => {
    const e = leererEintrag();
    e.quelle = "Homepage-Zugänge";
    e.kategorie = "hosting";
    e.typ = "Homepage / Hosting";
    const [erste, ...weitere] = zs;
    const a = erste[0] || "";
    if (istUrl(a)) e.url = /^https?:\/\//i.test(a) ? a : `https://${a.replace(/^www\./i, "www.")}`;
    e.titel = a ? a.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "") : "Ohne Titel";
    e.organisation = erste[1] || "";
    e.passwort = erste[2] || "";
    e.benutzer = erste[3] || "";
    const notizen = [];
    erste.forEach((v, i) => { if (v && i > 3) notizen.push(`${spaltenName(i)}: ${v}`); });
    weitere.forEach((z) => {
      const teile = z.map((v, i) => (v ? `${spaltenName(i)}: ${v}` : "")).filter(Boolean);
      if (teile.length) notizen.push(teile.join(" | "));
    });
    if (!e.titel || e.titel === "Ohne Titel") e.titel = e.organisation || "Ohne Titel";
    e.notizen = notizen.join("\n");
    return e;
  });
}

// Erkennt die beiden Blätter der Passwort-Tabelle und liefert alle Einträge
export function arbeitsmappeImportieren(XLSX, arbeitsmappe) {
  const ergebnis = [];
  const info = [];
  arbeitsmappe.SheetNames.forEach((name) => {
    const blatt = arbeitsmappe.Sheets[name];
    const roh = XLSX.utils.sheet_to_json(blatt, { header: 1, defval: "" });
    const kopf = (roh[0] || []).map(sauber);
    if (kopf.includes("Passwort") && (kopf.includes("Bezeichnung") || kopf.includes("Benutzername"))) {
      const liste = listeImportieren(XLSX.utils.sheet_to_json(blatt, { defval: "" }).filter((r) => Object.values(r).some((v) => sauber(v))));
      ergebnis.push(...liste);
      info.push(`${name}: ${liste.length} Einträge (Passwortliste)`);
    } else if (roh.length > 1) {
      const liste = homepageImportieren(roh);
      ergebnis.push(...liste);
      info.push(`${name}: ${liste.length} Einträge (Homepage-/Hosting-Zugänge)`);
    }
  });
  return { eintraege: ergebnis, info };
}
